import React from 'react';

/**
 * BrandLogo
 * =========
 * Renders the official "My Hometown Direct Care" logo consistently across
 * marketing pages and dashboards.
 *
 * Wave 42.334 — Aug 2026: split the logo by `variant` so the public
 * marketing website (Header/Footer/LandingNavbar/LandingFooter — no
 * `variant` passed) keeps the original banner, while internal app screens
 * (dashboard sidebar + all login/signup pages, via `variant="app"`) render
 * the newly uploaded v2 banner. Both are served from the app's own
 * `public/` so we don't depend on an external CDN URL that could disappear.
 *
 *  - default (light backgrounds)
 *  - `light` prop: wraps the logo in a white pill so the red heart and dark
 *    "Direct Care" text stay legible on dark sections.
 *  - `height` (px) controls size; width auto-scales. Default bumped from
 *    40 → 56 so the hero navbar no longer feels top-heavy with whitespace.
 */
const LOGO_SRC = {
  website: '/hometowndirect-banner.png',
  app: '/hometowndirect-banner-v2.png',
};

const BrandLogo = ({ className = '', light = false, height = 56, heightClass = '', variant = 'website' }) => {
  const wrap = light
    ? 'inline-flex items-center bg-white rounded-lg px-2 py-1'
    : 'inline-flex items-center';
  // When `heightClass` (Tailwind responsive classes like "h-10 lg:h-[72px]") is
  // provided, use CSS classes so the logo scales cleanly across breakpoints.
  // Falls back to numeric `height` prop for legacy call-sites.
  const imgStyle = heightClass ? { width: 'auto' } : { height: `${height}px`, width: 'auto' };
  const imgClass = heightClass ? `block select-none w-auto ${heightClass}` : 'block select-none';
  return (
    <span className={`${wrap} ${className}`} data-testid="brand-logo">
      <img
        src={LOGO_SRC[variant] || LOGO_SRC.website}
        alt="My Hometown Direct Care — Hassle-Free Everyday Healthcare"
        style={imgStyle}
        className={imgClass}
        draggable={false}
      />
    </span>
  );
};

export default BrandLogo;
